import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { DEFAULT_LANGUAGE, LANGUAGE_KEY, LOCALE_KEY } from '~/i18n/constants';
import { SupportedLanguage } from '~/i18n/loadLocale';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { StorageService } from '~/services/StorageService/StorageService';

import { L1_LANGUAGES, LANGUAGES, LOCALES } from './constants';

export const LocaleSelect = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const hasL1Languages = useFlag(FlagType.L1Languages);

  const [updateLocalizationPreferences] =
    api.endpoints.updateLocalizationPreferences.useMutation();

  const rawLanguage = StorageService.getItem(LANGUAGE_KEY);
  const rawLocale = StorageService.getItem(LOCALE_KEY);

  const storedLanguage =
    rawLanguage && rawLanguage !== 'null'
      ? (rawLanguage as SupportedLanguage)
      : DEFAULT_LANGUAGE;

  const storedLocale =
    rawLocale && rawLocale !== 'null' ? rawLocale : 'system-default';

  const [language, setLanguage] = useState<SupportedLanguage>(storedLanguage);
  const [locale, setLocale] = useState<string>(storedLocale);

  const filteredLOCALES = LOCALES.filter(
    (locale) =>
      locale.value.startsWith(language.split('-')[0]) ||
      locale.value === 'system-default',
  );

  const disabled = rawLanguage === 'null' && language === storedLanguage;

  // Reset locale to default when language changes and current locale is no longer valid
  useEffect(() => {
    const isLocaleValid = filteredLOCALES.some(
      (option) => option.value === locale,
    );
    if (!isLocaleValid) {
      setLocale('system-default');
    }
  }, [language, locale, filteredLOCALES]);

  const handleLanguageChange = (value: SupportedLanguage) => {
    setLanguage(value);
    setLocale('system-default');
  };

  const handleLocaleChange = (value: string) => {
    setLocale(value);
  };

  const handleSubmit = () => {
    toast({
      status: 'success',
      message: intl.formatMessage({
        id: 'LocaleSelect.selected',
        defaultMessage: 'Settings refreshing...',
        description:
          'Toast message when settings are refreshed. Example: language = "English" and locale = "en-US"',
      }),
    });

    updateLocalizationPreferences({ language, locale });
    setTimeout(() => {
      dispatch(slices.i18n.actions.setLanguage(language));
      dispatch(slices.i18n.actions.setLocale(locale));
      dispatch(api.util.resetApiState());
    }, 1000);
  };

  return (
    <Card
      mode="bordered"
      size="s"
      header={{
        title: intl.formatMessage({
          id: 'LocaleSelect.globalPreferences',
          defaultMessage: 'Global Preferences',
          description: 'Title for global preferences card',
        }),
      }}
    >
      <Layout preset="form-fields" w="25%" align="flex-start">
        <FormField<SupportedLanguage, false>
          label={intl.formatMessage({
            id: 'LocaleSelect.language',
            defaultMessage: 'Select Language',
            description: 'Label for language select',
          })}
          input={Select}
          inputProps={{
            options: hasL1Languages
              ? [...LANGUAGES, ...L1_LANGUAGES]
              : LANGUAGES,
            isClearable: false,
          }}
          name="language"
          value={language}
          onChange={(value) => handleLanguageChange(value ?? DEFAULT_LANGUAGE)}
        />

        <FormField<string, false>
          label={intl.formatMessage({
            id: 'LocaleSelect.locale',
            defaultMessage: 'Preferred Locale',
            description: 'Label for locale select',
          })}
          input={Select}
          info={intl.formatMessage({
            id: 'LocaleSelect.localeInfoWhenLanguageChanged',
            defaultMessage:
              'Changing the selected language will reset the locale to the browser default.',
            description: 'Info for locale select when language is changed',
          })}
          disabled={disabled}
          tooltip={
            disabled
              ? intl.formatMessage({
                  id: 'LocaleSelect.languageTooltip',
                  defaultMessage: 'Please select a language first',
                  description:
                    'Tooltip for locale select when no language is selected',
                })
              : undefined
          }
          inputProps={{
            options: filteredLOCALES,
            isClearable: false,
          }}
          name="locale"
          value={locale}
          onChange={(value) => handleLocaleChange(value ?? 'system-default')}
        />

        <Box alignSelf="flex-start">
          <Button
            iconPosition="left"
            level="primary"
            text={intl.formatMessage({
              id: 'LocaleSelect.savePreferences',
              defaultMessage: 'Save Preferences',
              description: 'Button text to save preferences',
            })}
            disabled={language === storedLanguage && locale === storedLocale}
            onClick={handleSubmit}
          />
        </Box>
      </Layout>
    </Card>
  );
};
